import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SPAIN_ARGENTINA_POST } from '../config/featuredBlogPosts';
import { formatBlogDate } from '../utils/blogUtils';
import { trackTeamDividerCta } from '../utils/analytics';
import { soccerPath } from '../utils/paths';
import BlogShareBar from './BlogShareBar';
import { useBlogPostMeta } from '../hooks/useBlogPostMeta';
import './SpainArgentinaBlogArticle.css';

const winnerLabel = {
  spain: 'Spain',
  argentina: 'Argentina',
  draw: 'Draw',
};

const SpainArgentinaBlogArticle = () => {
  const post = SPAIN_ARGENTINA_POST;
  const heroRef = useRef(null);
  const statsRef = useRef(null);

  useBlogPostMeta(post);

  useEffect(() => {
    const hero = heroRef.current;
    const stats = statsRef.current;
    if (!hero) return undefined;

    const onScroll = () => {
      const offset = Math.min(window.scrollY * 0.25, 80);
      hero.style.setProperty('--hero-shift', `${offset}px`);
      if (stats) {
        const rect = stats.getBoundingClientRect();
        const visible = rect.top < window.innerHeight * 0.85;
        stats.classList.toggle('esp-arg-article__stats--visible', visible);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <article className="esp-arg-article">
      <header className="esp-arg-article__hero" ref={heroRef}>
        <div
          className="esp-arg-article__hero-bg"
          style={{ backgroundImage: `url(${post.images.hero})` }}
          aria-hidden="true"
        />
        <div className="esp-arg-article__hero-scrim" aria-hidden="true" />
        <div className="esp-arg-article__hero-content">
          <Link to="/blog" className="esp-arg-article__back">
            ← Back to blog
          </Link>
          <p className="esp-arg-article__kicker">
            <span className="esp-arg-article__badge">Featured</span>
            <span>{formatBlogDate(post.publishedAt)}</span>
            <span>By {post.authorName}</span>
          </p>
          <h1>{post.title}</h1>
          <p className="esp-arg-article__excerpt">{post.excerpt}</p>
        </div>
      </header>

      <div className="esp-arg-article__shell">
        <section
          className="esp-arg-article__stats"
          ref={statsRef}
          aria-label="Head-to-head summary"
        >
          <div className="esp-arg-article__stat-card">
            <span className="esp-arg-article__stat-value">{post.stats.totalMeetings}</span>
            <span className="esp-arg-article__stat-label">Meetings</span>
          </div>
          <div className="esp-arg-article__stat-card esp-arg-article__stat-card--spain">
            <span className="esp-arg-article__stat-value">{post.stats.spainWins}</span>
            <span className="esp-arg-article__stat-label">Spain wins</span>
          </div>
          <div className="esp-arg-article__stat-card esp-arg-article__stat-card--draw">
            <span className="esp-arg-article__stat-value">{post.stats.draws}</span>
            <span className="esp-arg-article__stat-label">Draws</span>
          </div>
          <div className="esp-arg-article__stat-card esp-arg-article__stat-card--argentina">
            <span className="esp-arg-article__stat-value">{post.stats.argentinaWins}</span>
            <span className="esp-arg-article__stat-label">Argentina wins</span>
          </div>
        </section>

        <section className="esp-arg-article__panel">
          <h2>A rivalry that looks like a coin flip</h2>
          <p>
            Spain vs Argentina is one of international football&apos;s most evenly matched
            rivalries — and for decades, almost nobody noticed. Across{' '}
            <strong>{post.stats.totalMeetings} senior meetings</strong>, the record is
            perfectly balanced: six wins each and two draws.
          </p>
          <p>
            Thirteen of those games were friendlies. Only{' '}
            <strong>one</strong> was at a World Cup. That makes every new meeting feel bigger
            than the history books suggest — because the history books are basically a coin
            flip.
          </p>
          <div className="esp-arg-article__chip-row">
            <span className="esp-arg-article__chip">
              World Cup meetings: {post.stats.worldCupMeetings}
            </span>
            <span className="esp-arg-article__chip esp-arg-article__chip--spain">
              Biggest Spain win: {post.stats.biggestSpainWin}
            </span>
            <span className="esp-arg-article__chip esp-arg-article__chip--argentina">
              Biggest Argentina win: {post.stats.biggestArgentinaWin}
            </span>
          </div>
        </section>

        <section className="esp-arg-article__split">
          <div className="esp-arg-article__panel">
            <h2>The only World Cup meeting: England 1966</h2>
            <p>
              <strong>Argentina 2–1 Spain</strong> (13 July 1966, Group 2). Luis Artime scored
              both Argentina goals; Pirri equalized for Spain. Argentina advanced; Spain went
              out in the group stage.
            </p>
            <p>
              Until a modern tournament rematch, that single result was the entire World Cup
              chapter between them — a reminder that “history” in this fixture is short on
              knockout drama and long on friendly firepower.
            </p>
          </div>
          <figure className="esp-arg-article__figure">
            <img src={post.images.crowd} alt="Soccer crowd in a packed stadium" loading="lazy" />
            <figcaption>Atmosphere over archive: most Spain–Argentina meetings were friendlies.</figcaption>
          </figure>
        </section>

        <section className="esp-arg-article__split esp-arg-article__split--reverse">
          <figure className="esp-arg-article__figure">
            <img src={post.images.pitch} alt="Players contesting a ball on a green pitch" loading="lazy" />
            <figcaption>Modern Spain have largely had Argentina&apos;s number in recent decades.</figcaption>
          </figure>
          <div className="esp-arg-article__panel">
            <h2>How the rivalry tilted over time</h2>
            <p>
              <strong>Early edge: Argentina.</strong> From 1952–1966, Argentina won four of
              the first five meetings and kept clean sheets in the first three.
            </p>
            <p>
              <strong>Modern edge: Spain.</strong> In the last nine meetings before 2026,
              Spain lost only twice and won five — including the most lopsided scoreline in
              the series, the 6–1 in Madrid.
            </p>
          </div>
        </section>

        <section className="esp-arg-article__panel">
          <h2>Every Spain vs Argentina result</h2>
          <div className="esp-arg-article__table-wrap">
            <table className="esp-arg-article__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Competition</th>
                  <th>Result</th>
                  <th>Edge</th>
                </tr>
              </thead>
              <tbody>
                {post.results.map((row) => (
                  <tr key={`${row.date}-${row.result}`}>
                    <td>{row.date}</td>
                    <td>
                      <span
                        className={
                          row.competition === 'World Cup'
                            ? 'esp-arg-article__comp esp-arg-article__comp--wc'
                            : 'esp-arg-article__comp'
                        }
                      >
                        {row.competition}
                      </span>
                    </td>
                    <td>{row.result}</td>
                    <td>
                      <span className={`esp-arg-article__edge esp-arg-article__edge--${row.winner}`}>
                        {winnerLabel[row.winner]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="esp-arg-article__panel">
          <h2>Two friendlies that still define the narrative</h2>
          <div className="esp-arg-article__highlights">
            <div className="esp-arg-article__highlight esp-arg-article__highlight--argentina">
              <h3>Argentina 4–1 Spain (2010)</h3>
              <p>
                Messi, Higuaín, Tevez, and Agüero tore through a Spain side that had just won
                Euro 2008 and would win the World Cup weeks later. Pedigree doesn&apos;t
                guarantee a night.
              </p>
            </div>
            <div className="esp-arg-article__highlight esp-arg-article__highlight--spain">
              <h3>Spain 6–1 Argentina (2018)</h3>
              <p>
                Isco&apos;s hat-trick in Madrid. Argentina&apos;s heaviest defeat in decades.
                Messi missed the match through injury — a reminder that head-to-head numbers
                can hide context.
              </p>
            </div>
          </div>
        </section>

        <section className="esp-arg-article__mosaic" aria-label="Photo gallery">
          <img src={post.images.night} alt="Floodlit soccer stadium at night" loading="lazy" />
          <img src={post.images.training} alt="Soccer ball on the pitch during training" loading="lazy" />
          <img src={post.images.crowd} alt="Fans celebrating in the stands" loading="lazy" />
        </section>

        <section className="esp-arg-article__panel">
          <h2>What the numbers mean for amateur football</h2>
          <p>
            National-team rivalries look glamorous. At practice, they look like a scheduling
            problem: one side gets the star attackers, the other gets midfield control, and
            someone always ends up on the “weaker” team.
          </p>
          <p>
            That&apos;s why balanced division matters more than jersey color — rate skill,
            respect positions, and let the numbers do the arguing.
          </p>
        </section>

        <section className="esp-arg-article__panel">
          <h2>Rate these players — then re-simulate with our divider</h2>
          <p>
            Treat a Spain–Argentina fantasy rematch like a practice night. Rate each player
            1–10, assign positions, and let the Soccer app split fair teams.
          </p>
          <div className="esp-arg-article__table-wrap">
            <table className="esp-arg-article__table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Side</th>
                  <th>Position</th>
                  <th>Skill</th>
                </tr>
              </thead>
              <tbody>
                {post.playerRatings.map((row) => (
                  <tr key={row.player}>
                    <td>{row.player}</td>
                    <td>
                      <span
                        className={`esp-arg-article__edge esp-arg-article__edge--${
                          row.side === 'Spain' ? 'spain' : 'argentina'
                        }`}
                      >
                        {row.side}
                      </span>
                    </td>
                    <td>{row.position}</td>
                    <td>
                      <span className="esp-arg-article__skill">{row.skill}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ol className="esp-arg-article__steps">
            <li>Add ~16–22 players with name, position, and skill</li>
            <li>Auto-divide into two balanced sides</li>
            <li>Share the teams with your group</li>
          </ol>
        </section>

        <section className="esp-arg-article__panel esp-arg-article__share">
          <BlogShareBar post={post} />
        </section>

        <section className="esp-arg-article__cta">
          <div
            className="esp-arg-article__cta-bg"
            style={{ backgroundImage: `url(${post.images.pitch})` }}
            aria-hidden="true"
          />
          <div className="esp-arg-article__cta-body">
            <h2>Want Spain vs Argentina vibes without the arguments?</h2>
            <p>
              Open Soccer and re-simulate the clash with your own ratings.
            </p>
            <Link
              to={soccerPath('/')}
              className="esp-arg-article__cta-btn"
              onClick={() => trackTeamDividerCta('spain_argentina_blog')}
            >
              Open Soccer →
            </Link>
          </div>
        </section>

        {post.referenceLinks?.length > 0 && (
          <section className="esp-arg-article__panel esp-arg-article__refs">
            <h2>Related links</h2>
            <ul>
              {post.referenceLinks.map((link) => (
                <li key={link.url}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </article>
  );
};

export default SpainArgentinaBlogArticle;
